import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Divider
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  CloudUpload as UploadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  VolumeUp as VolumeIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AutoAwesome as AIIcon,
  CloudDone as CloudDoneIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Timer as TimerIcon
} from '@mui/icons-material';

import { useGetPreSignedUrlAudioQuery } from '../../redux/thunks/files/filespresignurlaudio';
import { useGetUserProfileQuery } from '../../redux/thunks/users/userprofile';

// Audio quota constants
const AUDIO_QUOTAS = {
  free: {
    maxUploads: 1, // 1 upload per month
    maxDurationPerFile: 3, // 3 minutes per file
    maxTotalDuration: 3 // 3 minutes total per month
  },
  premium: {
    maxUploads: 5, // 5 uploads per month
    maxDurationPerFile: 5, // 5 minutes per file
    maxTotalDuration: 25 // 25 minutes total per month (5 files * 5 minutes)
  }
};

const BusinessAudioUpload = ({ 
  businessId, 
  businessName, 
  existingAudio = null,
  onSuccess,
  onCancel 
}) => {
  const userId = useSelector((state) => state.auth?.userId || null);
  const spId = useSelector((state) => state.auth?.user?.spId || null);

  // Get user data for quota checking
  const { data: userData, isError: userProfileError, isLoading: isUserLoading } = useGetUserProfileQuery(userId);

  const [recordingState, setRecordingState] = useState('idle'); // idle, recording, processing
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, success
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [error, setError] = useState('');
  const [showMethodDialog, setShowMethodDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [currentAudio, setCurrentAudio] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Audio quota calculations
  const userAccountType = userData?.accountDetails?.type || 'free';
  const audioQuotas = AUDIO_QUOTAS[userAccountType] || AUDIO_QUOTAS.free;
  
  // Get current month key (format: YYYY-M to match DB structure)
  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  }, []);

  // Calculate current audio usage
  const currentAudioUsage = useMemo(() => {
    if (!userData?.quotaUsage?.audio_descriptions) {
      return {
        monthlyUploads: 0,
        totalDurationMinutes: 0,
        monthlyDurationMinutes: 0
      };
    }

    const audioQuotaData = userData.quotaUsage.audio_descriptions;
    const monthlyData = audioQuotaData.monthly || {};
    
    // Get current month uploads and duration using the correct format
    const currentMonthUploads = monthlyData[currentMonthKey] || 0;
    const currentMonthDuration = monthlyData[`${currentMonthKey}_duration`] || 0;
    const totalDuration = audioQuotaData.totalDurationMinutes || 0;
    
    return {
      monthlyUploads: currentMonthUploads,
      totalDurationMinutes: totalDuration,
      monthlyDurationMinutes: currentMonthDuration
    };
  }, [userData, currentMonthKey]);

  // Check if user can upload more audio
  const canUploadMore = useMemo(() => {
    return currentAudioUsage.monthlyUploads < audioQuotas.maxUploads &&
           currentAudioUsage.monthlyDurationMinutes < audioQuotas.maxTotalDuration;
  }, [currentAudioUsage, audioQuotas]);

  // Check if current recording/file exceeds duration limit
  const exceedsDurationLimit = useMemo(() => {
    if (!currentAudio) return false;
    const durationMinutes = currentAudio.duration;
    return durationMinutes > audioQuotas.maxDurationPerFile;
  }, [currentAudio, audioQuotas]);

  // Get presigned URL for audio upload
  const { 
    data: audioPresignedData, 
    isLoading: isAudioUrlLoading, 
    error: audioUrlError 
  } = useGetPreSignedUrlAudioQuery(
    {
      eventType: 'BusinessEditAudio',
      audioType: currentAudio?.type || 'webm',
      businessId: businessId
    },
    { 
      skip: !currentAudio?.file // Only fetch when we have audio
    }
  );

  // Detect supported audio format for recording
  const detectSupportedFormat = () => {
    const formats = [
      { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
      { mimeType: 'audio/webm', extension: 'webm' },
      { mimeType: 'audio/mp4', extension: 'mp4' },
      { mimeType: 'audio/mp3', extension: 'mp3' },
      { mimeType: 'audio/ogg;codecs=vorbis', extension: 'ogg' },
    ];

    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format.mimeType)) {
        return format;
      }
    }
    return { mimeType: 'audio/webm', extension: 'webm' }; // fallback
  };

  // Check if browser supports recording
  const canRecord = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);

  const handleMethodSelection = () => {
    if (!canUploadMore) {
      setError(`You've reached your ${userAccountType} plan limit of ${audioQuotas.maxUploads} audio uploads per month.`);
      return;
    }
    setShowMethodDialog(true);
  };

  const handleMethodConfirm = () => {
    setShowMethodDialog(false);
    if (selectedMethod === 'record') {
      startRecording();
    } else if (selectedMethod === 'upload') {
      fileInputRef.current?.click();
    }
    setSelectedMethod('');
  };

  const startRecording = async () => {
    try {
      setError('');
      setRecordingState('recording');
      setRecordingDuration(0);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        }
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      const supportedFormat = detectSupportedFormat();
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: supportedFormat.mimeType,
        audioBitsPerSecond: 64000
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: supportedFormat.mimeType 
        });
        handleAudioComplete(audioBlob, supportedFormat.extension, 'recorded');
      };

      mediaRecorder.start(100);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 0.1;
          // Auto-stop if exceeding duration limit
          if (newDuration >= audioQuotas.maxDurationPerFile * 60) {
            stopRecording();
            setError(`Recording stopped: ${audioQuotas.maxDurationPerFile} minute limit reached for ${userAccountType} accounts.`);
          }
          return newDuration;
        });
      }, 100);

    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording. Please check microphone permissions.');
      setRecordingState('idle');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
      clearInterval(timerRef.current);
      setRecordingState('processing');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    
    // Validate file type
    const allowedTypes = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav', 'audio/mpeg', 'audio/mp3'];
    if (!allowedTypes.includes(file.type)) {
      setError('Unsupported file type. Please use WebM, MP4, OGG, WAV, or MP3 files.');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }

    setUploadState('uploading');
    
    // Get file extension
    const extension = file.name.split('.').pop()?.toLowerCase() || 'audio';
    
    handleAudioComplete(file, extension, 'uploaded');
  };

  const handleAudioComplete = async (audioData, extension, source) => {
    try {
      // Calculate duration (approximate for uploaded files)
      let duration = 0;
      if (source === 'recorded') {
        duration = recordingDuration / 60; // Convert to minutes
      } else {
        // For uploaded files, we'll estimate or you could use Web Audio API to get actual duration
        duration = audioData.size / (64000 / 8) / 60; // Rough estimate based on bitrate
      }

      // Check duration against quota
      if (duration > audioQuotas.maxDurationPerFile) {
        setError(`Audio duration (${Math.round(duration * 10) / 10} minutes) exceeds ${userAccountType} plan limit of ${audioQuotas.maxDurationPerFile} minutes per file.`);
        setRecordingState('idle');
        setUploadState('idle');
        return;
      }

      // Check if total monthly duration would be exceeded
      if (currentAudioUsage.monthlyDurationMinutes + duration > audioQuotas.maxTotalDuration) {
        setError(`This audio would exceed your monthly duration limit of ${audioQuotas.maxTotalDuration} minutes for ${userAccountType} accounts.`);
        setRecordingState('idle');
        setUploadState('idle');
        return;
      }

      // Create audio object
      const audioInfo = {
        file: audioData,
        duration: duration,
        type: extension,
        source: source, // 'recorded' or 'uploaded'
        size: audioData.size,
        timestamp: new Date().toISOString()
      };

      setCurrentAudio(audioInfo);
      setRecordingState('idle');
      setUploadState('idle');

    } catch (error) {
      console.error('Error processing audio:', error);
      setError('Failed to process audio. Please try again.');
      setRecordingState('idle');
      setUploadState('idle');
    }
  };

  const playAudio = () => {
    if (!currentAudio?.file) return;

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      const audioUrl = URL.createObjectURL(currentAudio.file);
      audioRef.current = new Audio(audioUrl);
      audioRef.current.play();
      setIsPlaying(true);

      audioRef.current.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };
    }
  };

  const removeAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    
    setCurrentAudio(null);
    setRecordingDuration(0);
    setError('');
    setUploadSuccess(false);
  };

  const uploadAudioToS3 = async (audioDescription, audioPresignedData) => {
    if (!audioDescription?.file || !audioPresignedData?.url) {
      return null;
    }

    try {
      const response = await fetch(audioPresignedData.url, {
        method: 'PUT',
        body: audioDescription.file,
        headers: {
          'Content-Type': `audio/${audioDescription.type}`,
          'x-amz-meta-eventtype': 'BusinessEditAudio',
          'x-amz-meta-audiotype': audioDescription.type,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to upload audio');
      }

      return true;
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw error;
    }
  };

  const handleUpload = async () => {
    if (!currentAudio?.file) {
      setError('No audio to upload');
      return;
    }

    if (!canUploadMore) {
      setError(`Upload limit reached for ${userAccountType} accounts (${audioQuotas.maxUploads} per month).`);
      return;
    }

    if (exceedsDurationLimit) {
      setError(`Audio duration exceeds ${userAccountType} plan limit of ${audioQuotas.maxDurationPerFile} minutes per file.`);
      return;
    }

    try {
      setUploadState('uploading');
      setError('');

      // Upload audio to S3 - S3 trigger will handle all metadata automatically
      if (audioPresignedData?.url) {
        await uploadAudioToS3(currentAudio, audioPresignedData);
        setUploadSuccess(true);
        setUploadState('success');
        
        // Success - S3 trigger handles metadata, transcription, etc.
        setTimeout(() => {
          onSuccess(currentAudio);
        }, 1500);
      } else {
        throw new Error('No upload URL available');
      }

    } catch (error) {
      console.error('Error uploading audio:', error);
      setError('Failed to upload audio. Please try again.');
      setUploadState('idle');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="primary" />
          Audio Description Tool
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Record or upload an audio description for {businessName}. Our AI will automatically transcribe and extract key information to enhance your business profile.
        </Typography>
      </Box>

      {/* Quota Usage Display */}
      <Card variant="outlined" sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="medium">
                Audio Quota Usage
              </Typography>
            </Box>
            <Chip 
              label={userAccountType === 'premium' ? 'Premium' : 'Free'}
              size="small"
              color={userAccountType === 'premium' ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Monthly Uploads
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {currentAudioUsage.monthlyUploads} / {audioQuotas.maxUploads}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Duration Limit per File
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {audioQuotas.maxDurationPerFile} minutes
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Monthly Duration
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {Math.round(currentAudioUsage.monthlyDurationMinutes * 10) / 10} / {audioQuotas.maxTotalDuration} min
              </Typography>
            </Box>
          </Box>

          {!canUploadMore && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2">
                You've reached your {userAccountType} plan audio limit. 
                {userAccountType === 'free' ? ' Upgrade to Premium for more uploads!' : ' Limits reset monthly.'}
              </Typography>
            </Alert>
          )}

          {userAccountType === 'free' && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Premium Benefits:</strong> 5 uploads/month, 5 minutes per file, 25 minutes total
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Upload errors */}
      {audioUrlError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Audio upload error: {audioUrlError.message || 'Failed to prepare audio upload'}
        </Alert>
      )}

      {/* Existing Audio Display */}
      {existingAudio && !currentAudio && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            You have an existing audio description. Upload a new one to replace it.
          </Typography>
        </Alert>
      )}

      {/* No Audio State */}
      {!currentAudio && recordingState === 'idle' && uploadState === 'idle' && (
        <Card variant="outlined" sx={{ borderStyle: 'dashed', bgcolor: 'grey.50' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <VolumeIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Add Audio Description
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Record with your microphone or upload an existing audio file
            </Typography>
            <Button
              variant="contained"
              onClick={handleMethodSelection}
              disabled={isAudioUrlLoading || !canUploadMore}
              size="large"
            >
              {!canUploadMore ? 'Quota Limit Reached' : 'Add Audio Description'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recording State */}
      {recordingState === 'recording' && (
        <Card variant="outlined" sx={{ bgcolor: 'error.light', borderColor: 'error.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: 'error.main',
                  animation: 'pulse 1.5s infinite'
                }} />
                <Typography variant="h6">Recording...</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" color="error.main">
                  {formatDuration(recordingDuration)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Max: {audioQuotas.maxDurationPerFile} min
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              color="error"
              onClick={stopRecording}
              startIcon={<StopIcon />}
              fullWidth
            >
              Stop Recording
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Processing State */}
      {recordingState === 'processing' && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Processing Audio...
            </Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Please wait while we process your audio description.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Uploading State */}
      {uploadState === 'uploading' && (
        <Card variant="outlined" sx={{ bgcolor: 'info.light', borderColor: 'info.main' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Uploading Audio...
            </Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Uploading to secure cloud storage. AI processing will begin automatically.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Upload Success State */}
      {uploadState === 'success' && (
        <Card variant="outlined" sx={{ bgcolor: 'success.light', borderColor: 'success.main' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <CloudDoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Upload Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your audio description has been uploaded and AI processing has started. 
              Changes will appear on your business profile shortly.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Audio Present State (Ready to Upload) */}
      {currentAudio && uploadState === 'idle' && recordingState === 'idle' && (
        <Card variant="outlined" sx={{ bgcolor: exceedsDurationLimit ? 'warning.light' : 'primary.light', borderColor: exceedsDurationLimit ? 'warning.main' : 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {exceedsDurationLimit ? (
                  <WarningIcon color="warning" />
                ) : (
                  <CheckIcon color="primary" />
                )}
                <Typography variant="h6">
                  {exceedsDurationLimit ? 'Duration Limit Exceeded' : 'Audio Ready'}
                </Typography>
              </Box>
              <IconButton onClick={removeAudio} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>

            {exceedsDurationLimit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This audio ({Math.round(currentAudio.duration * 10) / 10} minutes) exceeds your {userAccountType} plan limit of {audioQuotas.maxDurationPerFile} minutes per file.
                </Typography>
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip 
                label={`${formatDuration(currentAudio.duration * 60)}`}
                size="small" 
                color={exceedsDurationLimit ? "warning" : "primary"}
              />
              <Chip 
                label={formatFileSize(currentAudio.size)}
                size="small" 
              />
              <Chip 
                label={currentAudio.source === 'recorded' ? 'Recorded' : 'Uploaded'}
                size="small" 
                variant="outlined"
              />
              <Chip 
                label={currentAudio.type.toUpperCase()}
                size="small" 
                variant="outlined"
              />
            </Stack>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={playAudio}
                startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
                size="small"
              >
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleMethodSelection}
                startIcon={currentAudio.source === 'recorded' ? <UploadIcon /> : <MicIcon />}
                size="small"
                disabled={!canUploadMore}
              >
                {currentAudio.source === 'recorded' ? 'Upload Instead' : 'Record Instead'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                startIcon={<CloudDoneIcon />}
                disabled={isAudioUrlLoading || !canUploadMore || exceedsDurationLimit}
                fullWidth
              >
                {isAudioUrlLoading ? 'Preparing Upload...' : 
                 !canUploadMore ? 'Upload Quota Reached' :
                 exceedsDurationLimit ? 'Duration Limit Exceeded' :
                 'Upload Audio Description'}
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
              >
                Cancel
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Method Selection Dialog */}
      <Dialog open={showMethodDialog} onClose={() => setShowMethodDialog(false)}>
        <DialogTitle>Choose Audio Method</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <FormLabel component="legend">How would you like to add your audio description?</FormLabel>
            <RadioGroup
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              sx={{ mt: 2 }}
            >
              {canRecord && (
                <FormControlLabel
                  value="record"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Record with Microphone</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Record directly using your device's microphone (Max: {audioQuotas.maxDurationPerFile} minutes)
                      </Typography>
                    </Box>
                  }
                />
              )}
              <FormControlLabel
                value="upload"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Upload Audio File</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload a pre-recorded audio file (WebM, MP4, OGG, WAV, MP3) - Max: {audioQuotas.maxDurationPerFile} minutes
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMethodDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleMethodConfirm} 
            variant="contained"
            disabled={!selectedMethod}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      {/* CSS Animation for recording pulse */}
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default BusinessAudioUpload;